<template>
  <v-dialog v-model="dialog" max-width="700px" @keydown.esc="close">
    <v-card class="elevation-8">
      <v-toolbar dark color="primary" dense>
        <v-toolbar-title>Descargar Gafete QR</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- Vista previa del gafete -->
        <div class="badge-preview">
          <div ref="badgeContainer" class="badge-content">
            <!-- Encabezado con logo -->
            <div class="badge-header">
              <img
                v-if="logoDataUrl"
                :src="logoDataUrl"
                alt="Logo empresa"
                class="company-logo"
              />
              <div v-else class="logo-placeholder">LOGO</div>
            </div>

            <!-- QR Code -->
            <div class="badge-qr-container">
              <canvas ref="qrCanvas" class="qr-canvas"></canvas>
            </div>

            <!-- Información del transportista -->
            <div class="badge-footer">
              <p class="transport-name">{{ transport.name }}</p>
              <p class="driver-name">{{ driverFullName }}</p>
              <p class="plate-number">{{ transport.plate }}</p>
              <p class="qr-code-text">{{ transport.qr_code }}</p>
            </div>

            <!-- Pie de página -->
            <div class="badge-company-footer">
              <p class="company-name">{{ companyName }}</p>
            </div>
          </div>
        </div>

        <!-- Opciones de descarga -->
        <div class="mt-4 text-center">
          <p class="grey--text text-sm mb-3">
            Selecciona el formato de descarga
          </p>
          <div class="button-group">
            <v-btn
              color="primary"
              @click="downloadAsImage"
              :loading="downloading"
              class="mr-2"
            >
              <v-icon left>image</v-icon>
              Descargar PNG
            </v-btn>
            <v-btn
              color="success"
              @click="downloadAsPDF"
              :loading="downloading"
            >
              <v-icon left>picture_as_pdf</v-icon>
              Descargar PDF
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="close">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QRCode from 'qrcode'
import jsPDF from 'jspdf'

export default {
  name: 'QRBadgeDownload',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    transport: {
      type: Object,
      required: true
    },
    companyName: {
      type: String,
      default: ''
    },
    companyLogo: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      dialog: false,
      downloading: false,
      logoDataUrl: null
    }
  },
  computed: {
    driverFullName() {
      return [this.transport.driver_name, this.transport.driver_last_name]
        .filter(Boolean)
        .join(' ')
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.dialog = val
        if (val) {
          this.$nextTick(() => {
            this.generateQR()
          })
          this.preloadLogo()
        }
      }
    },
    dialog(val) {
      if (!val) {
        this.$emit('close')
      }
    }
  },
  methods: {
    async preloadLogo() {
      if (!this.companyLogo) {
        this.logoDataUrl = null
        return
      }

      if (this.companyLogo.startsWith('data:')) {
        this.logoDataUrl = this.companyLogo
        return
      }

      try {
        const response = await fetch(this.companyLogo, { mode: 'cors' })
        const blob = await response.blob()
        this.logoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('No se pudo cargar el logo de la empresa (posible bloqueo CORS):', error)
        this.logoDataUrl = null
      }
    },
    async generateQR() {
      try {
        await QRCode.toCanvas(
          this.$refs.qrCanvas,
          this.transport.qr_code,
          {
            width: 220,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          }
        )
      } catch (error) {
        console.error('Error generando QR:', error)
      }
    },
    async downloadAsImage() {
      this.downloading = true
      try {
        const html2canvas = (await import('html2canvas')).default
        const canvas = await html2canvas(this.$refs.badgeContainer, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true
        })

        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/png')
        link.download = `gafete-qr-${this.transport.plate.replace(/\s+/g, '-')}.png`
        link.click()

        this.$emit('download-success', 'Gafete descargado exitosamente')
      } catch (error) {
        console.error('Error descargando imagen:', error)
        this.$emit('download-error', 'Error al descargar el gafete')
      } finally {
        this.downloading = false
      }
    },
    async downloadAsPDF() {
      this.downloading = true
      try {
        const html2canvas = (await import('html2canvas')).default
        const canvas = await html2canvas(this.$refs.badgeContainer, {
          scale: 1.5,
          backgroundColor: '#ffffff',
          useCORS: true
        })

        const imgData = canvas.toDataURL('image/png')
        const imgWidth = 100
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        })

        const x = (doc.internal.pageSize.getWidth() - imgWidth) / 2
        const y = 40

        doc.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)
        doc.save(`gafete-qr-${this.transport.plate.replace(/\s+/g, '-')}.pdf`)

        this.$emit('download-success', 'PDF descargado exitosamente')
      } catch (error) {
        console.error('Error descargando PDF:', error)
        this.$emit('download-error', 'Error al descargar el PDF')
      } finally {
        this.downloading = false
      }
    },
    close() {
      this.dialog = false
    }
  }
}
</script>

<style scoped>
.badge-preview {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 350px;
}

.badge-content {
  background: white;
  width: 280px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.badge-header {
  background: #1976d2;
  padding: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60px;
}

.company-logo {
  max-width: 100%;
  max-height: 45px;
  object-fit: contain;
}

.logo-placeholder {
  color: white;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.badge-qr-container {
  padding: 20px 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fafafa;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.qr-canvas {
  max-width: 100%;
  height: auto;
  border: 2px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  background: white;
}

.badge-footer {
  padding: 15px 12px;
  text-align: center;
}

.transport-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.driver-name {
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
  margin: 0 0 3px 0;
}

.plate-number {
  font-size: 11px;
  color: #666;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  margin: 0 0 4px 0;
  letter-spacing: 0.5px;
}

.qr-code-text {
  font-size: 9px;
  color: #999;
  margin: 0;
  word-break: break-all;
}

.badge-company-footer {
  background: #f9f9f9;
  padding: 8px;
  border-top: 1px solid #eee;
  text-align: center;
}

.company-name {
  font-size: 10px;
  color: #999;
  margin: 0;
  font-weight: 500;
}

.button-group {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.text-sm {
  font-size: 12px !important;
}

@media (max-width: 600px) {
  .badge-content {
    width: 280px;
  }

  .button-group {
    flex-direction: column;
  }

  .v-btn {
    width: 100%;
  }
}
</style>
